import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { useWishlist } from '../contexts/WishlistContext';
import { useAuth } from '../contexts/AuthContext';
import { useGeo } from '../contexts/GeoContext';
import { toast } from 'sonner';
import { getSwatchStyle } from '../lib/colorMap';
import { card as optCard } from '../lib/imageUrl';
import { trackProductClick } from '../lib/analytics';

export default function ProductCard({ product }) {
  const { user } = useAuth();
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { formatPrice } = useGeo();
  const inWishlist = isInWishlist(product.id);

  const handleClick = () => {
    trackProductClick(product.id, { name: product.name, category: product.category, price: product.price });
  };

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error('Please login to add items to wishlist');
      return;
    }

    if (inWishlist) {
      const result = await removeFromWishlist(product.id);
      if (result.success) {
        toast.success('Removed from wishlist');
      }
    } else {
      const result = await addToWishlist(product.id);
      if (result.success) {
        toast.success('Added to wishlist');
      }
    }
  };

  const discountedPrice = product.is_promo && product.promo_percentage
    ? product.price * (1 - product.promo_percentage / 100)
    : null;

  return (
    <Link
      to={`/product/${product.id}`}
      onClick={handleClick}
      className="card-brutalist group block"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image Container */}
      <div className="relative aspect-[3/4] bg-[#F5F5F5] border-b-2 border-[#0A0A0A] overflow-hidden image-zoom">
        {product.images?.[0] ? (
          <img
            src={optCard(product.images[0])}
            alt={product.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#525252]">
            No Image
          </div>
        )}

        {/* Promo Badge */}
        {product.stock === 0 ? (
          <span className="absolute top-3 left-3 bg-[#0A0A0A] text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 z-10">
            Sold Out
          </span>
        ) : product.is_promo && product.promo_percentage ? (
          <span className="badge-promo absolute top-3 left-3">
            -{product.promo_percentage}%
          </span>
        ) : null}

        {/* Dress-up badge — flatlays showing the full look together */}
        {product.is_dress_up && product.stock > 0 && (
          <span
            data-testid={`dress-up-badge-${product.id}`}
            className="absolute top-3 left-3 bg-[#FFC700] text-[#0A0A0A] border-2 border-[#0A0A0A] text-[10px] font-bold uppercase tracking-wider px-2 py-1 z-10 shadow-[2px_2px_0px_#0A0A0A]"
          >
            Dress Up — Full Fit
          </span>
        )}

        {/* Wishlist Button */}
        <button
          onClick={handleWishlistToggle}
          data-testid={`wishlist-toggle-${product.id}`}
          className={`absolute top-3 right-3 p-2 border-2 border-[#0A0A0A] transition-all ${
            inWishlist 
              ? 'bg-[#FAD4D4]' 
              : 'bg-white opacity-0 group-hover:opacity-100'
          }`}
        >
          <Heart className={`w-4 h-4 ${inWishlist ? 'fill-current' : ''}`} />
        </button>

        {/* Quick View Overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-end justify-center pb-4 opacity-0 group-hover:opacity-100">
          <span className="bg-white border-2 border-[#0A0A0A] px-4 py-2 text-sm font-semibold">
            Quick View
          </span>
        </div>
      </div>

      {/* Product Info */}
      <div className="p-3">
        <p className="text-[10px] text-[#525252] uppercase tracking-wider mb-0.5">
          {product.category}
        </p>
        <h3 className="font-semibold text-xs sm:text-sm leading-tight mb-1.5 group-hover:underline line-clamp-2">
          {product.name}
        </h3>
        <div className="flex items-center gap-2">
          {discountedPrice ? (
            <>
              <span className="font-bold text-sm sm:text-base price-sale">
                {formatPrice(discountedPrice)}
              </span>
              <span className="price-original text-xs">
                {formatPrice(product.price)}
              </span>
            </>
          ) : (
            <span className="font-bold text-sm sm:text-base">
              {formatPrice(product.price)}
            </span>
          )}
        </div>

        {/* Sold-as-seen: show full colorway as single split-swatch + label */}
        {product.colors && product.colors.length > 0 && (
          <div className="flex items-center gap-1.5 mt-2">
            <span
              className="w-4 h-4 rounded-full border border-[#0A0A0A] flex-shrink-0"
              style={getSwatchStyle(product.colors.join('/'))}
              title={product.colors.join(' / ')}
              data-testid={`card-swatch-${product.id}`}
            />
            <span className="text-[10px] text-[#525252] truncate">{product.colors.join(' / ')}</span>
          </div>
        )}
      </div>
    </Link>
  );
}
